import classNames from "classnames";
import { motion } from "framer-motion";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Droppable, DroppableProvided, DropResult } from "react-beautiful-dnd";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BoardApi } from "../../../services/board-service-api";
import { withManagement } from "../../../shared/hoc/with-management";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import { copyTextToClipboard } from "../../ui-kit/kuika-copy-button/copy-text-to-clipboard";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { toastService } from "../../ui-kit/kuika-toast/toast-service";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import AiSparkleIcon from "./ai-sparkle-icon";
import ArchivedIssuesModal from "./archived-issues-modal";
import AskAiModal from "./ask-ai-modal";
import BoardBacklogView from "./board-backlog-view";
import BoardColumn, { ColumnApi, ColumnGroupBy } from "./board-column";
import {
  ResolvedColumn,
  boardAvailableStatuses,
  boardColumns,
  columnIdForStatus,
  resolveDropStatus
} from "./board-columns";
import { connectBoardEventStream, disconnectBoardEventStream } from "./board-events-stream";
import BoardImportWizard from "./board-import-wizard";
import BoardListView from "./board-list-view";
import BoardLoading from "./board-loading";
import BoardReportsView from "./board-reports-view";
import BoardRoadmapView from "./board-roadmap-view";
import { buildBoardPath, findBoardBySlug } from "./board-routing";
import { BoardService } from "./board-service";
import BoardSettingsPage from "./board-settings-page";
import { issueHeaderChanged } from "./change-event-routing";
import CommandPalette from "./command-palette";
import { confirmDialog, ConfirmDialogHost, promptDialog } from "./confirm-service";
import ContextMenu, { ContextMenuItem } from "./context-menu";
import { currentUserId } from "./current-user";
import { Density, getStoredDensity, setStoredDensity } from "./density";
import FilterChips, { BoardFilters } from "./filter-chips";
import { BoardCardIssue } from "./issue-card";
import { NewEpicChildren } from "./issue-detail";
import IssuePanel from "./issue-panel";
import JqlBar from "./jql-bar";
import { JqlSuggestContext } from "./jql-suggest";
import NotificationsPanel from "./notifications-panel";
import { DirectoryMember, getMembers, loadPeopleDirectory, subscribePeople } from "./people-directory";
import { SavedViews } from "./saved-views";
import ShortcutsOverlay from "./shortcuts-overlay";
import SuggestedLane from "./suggested-lane";
import {
  buildSuggestions,
  getDismissedSuggestions,
  getSuggestionsHidden,
  pruneDismissedSuggestions,
  setDismissedSuggestions,
  setSuggestionsHidden,
  Suggestion
} from "./suggestions";
import { allowedTargetStatusIds, isEdgeAllowed } from "./transitions";
import {
  Board,
  BoardChangeEvent,
  BoardColumn as BoardColumnDef,
  BoardStatus,
  EpicCard,
  ISSUE_TYPE_LABELS,
  Issue,
  UpdateIssueInput,
  IssueCard as IssueCardData,
  IssuePriority,
  IssueType,
  LabelDef,
  PRIORITY_ORDER,
  Project,
  StatusCategory,
  boardProjectKeys
} from "./types";
import { useBoardConfig } from "./use-board";
import { useBoardAiEnabled } from "./use-board-ai";
import { useBoardStore } from "./use-board-store";
import { ColumnFilters } from "./use-column-issues";
import "./board.scss";

interface FocusState {
  // The focused column (board column id), not a status — a column may aggregate several statuses.
  columnId: string;
  issueId: string | null;
}

// Anything with a human key can be navigated to (full Issue or a light card).
interface IssueKeyRef {
  key: string;
}

interface BoardRouteParams {
  domainName: string;
  boardSlug?: string;
  issueKey?: string;
}

// Named registry of elements the board focuses imperatively (keyboard shortcuts). Add a key per
// target; wire it with a callback ref and focus via boardRefs.current.<name>.
interface BoardElementRefs {
  filter: HTMLInputElement | HTMLTextAreaElement | null;
}

// A keyboard column-move waiting to be persisted. Rapid Shift+J/K presses update the optimistic UI
// every press but coalesce into ONE backend write to the final column after the burst settles.
interface PendingMove {
  issueId: string;
  destinationStatusId: string;
  destinationIndex: number;
  // The column at the START of the burst, so the single coalesced write/event is origin→final and
  // the intermediate columns the card passed through never reach the backend.
  sourceStatusId: string;
}

// How long the keyboard move waits for more presses before persisting.
const MOVE_DEBOUNCE_MS: number = 350;

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag: string = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || target.isContentEditable;
}

function statusDotColor(category: "todo" | "in_progress" | "done"): string {
  switch (category) {
    case "todo":
      return "var(--dark-4)";
    case "in_progress":
      return "var(--blue-5)";
    case "done":
      return "var(--green-6)";
  }
}

// Seed a full Issue from a light card so the drawer can open INSTANTLY on click with the
// header/fields we already have; the by-key fetch then replaces it with the authoritative
// issue (description, custom fields, estimates), and the detail bundle fills comments/activity.
function issueFromCard(card: IssueCardData): Issue {
  return {
    id: card.id,
    key: card.key,
    projectKey: card.projectKey,
    type: card.type,
    statusId: card.statusId,
    summary: card.summary,
    descriptionMarkdown: "",
    priority: card.priority,
    assigneeUserId: card.assigneeUserId,
    reporterUserId: card.reporterUserId,
    labels: card.labels,
    storyPoints: card.storyPoints,
    startDate: card.startDate,
    dueDate: card.dueDate,
    parentIssueId: card.parentIssueId,
    epicId: card.epicId,
    sprintId: card.sprintId,
    watcherUserIds: card.watcherUserIds,
    archivedAt: card.archivedAt,
    epicName: null,
    epicColor: null,
    originalEstimateMin: null,
    remainingEstimateMin: null,
    timeSpentMin: null,
    fixVersionId: card.fixVersionId,
    customFields: {},
    rank: card.rank,
    createdAt: card.createdAt,
    updatedAt: card.updatedAt
  };
}

const BoardPage: React.FC = () => {
  const { config, loaded: boardLoaded } = useBoardConfig();
  const params = useParams<Partial<BoardRouteParams>>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { domainName, boardSlug: urlBoardSlug, issueKey: urlIssueKey } = params;
  const viewParam: string | null = searchParams.get("view");
  type BoardView = "board" | "list" | "backlog" | "roadmap" | "reports" | "settings";
  const view: BoardView =
    viewParam === "list"
      ? "list"
      : viewParam === "backlog"
        ? "backlog"
        : viewParam === "roadmap"
          ? "roadmap"
          : viewParam === "reports"
            ? "reports"
            : viewParam === "settings"
              ? "settings"
              : "board";
  // Admin views (full-page config) hide the data toolbar (filter chips, JQL).
  const isAdminView: boolean = view === "settings";
  const setView = useCallback(
    (next: BoardView): void => {
      const sp: URLSearchParams = new URLSearchParams(searchParams);
      if (next === "board") sp.delete("view");
      else sp.set("view", next);
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  type GroupBy = "none" | "assignee" | "priority" | "epic";
  const groupBy: GroupBy = (() => {
    const value: string | null = searchParams.get("groupBy");
    if (value === "assignee" || value === "priority" || value === "epic") return value;
    return "none";
  })();
  const setGroupBy = (next: GroupBy): void => {
    const sp: URLSearchParams = new URLSearchParams(searchParams);
    if (next === "none") sp.delete("groupBy");
    else sp.set("groupBy", next);
    setSearchParams(sp, { replace: true });
  };

  const filters: BoardFilters = useMemo(
    () => ({
      assignee: searchParams.get("assignee"),
      priority: (searchParams.get("priority") as IssuePriority | null) ?? null,
      type: (searchParams.get("type") as IssueType | null) ?? null
    }),
    [searchParams]
  );

  const setFilters = (next: BoardFilters): void => {
    const sp: URLSearchParams = new URLSearchParams(searchParams);
    if (next.assignee) sp.set("assignee", next.assignee);
    else sp.delete("assignee");
    if (next.priority) sp.set("priority", next.priority);
    else sp.delete("priority");
    if (next.type) sp.set("type", next.type);
    else sp.delete("type");
    setSearchParams(sp, { replace: true });
  };

  const jqlInput: string = searchParams.get("jql") ?? "";

  // Stable so the JQL bar's debounce effect doesn't re-arm on unrelated search-param churn.
  const setJqlInput = useCallback(
    (next: string): void => {
      setSearchParams(
        (prev: URLSearchParams) => {
          const sp: URLSearchParams = new URLSearchParams(prev);
          if (next.trim()) sp.set("jql", next);
          else sp.delete("jql");
          return sp;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearFilters = (): void => {
    const sp: URLSearchParams = new URLSearchParams(searchParams);
    sp.delete("assignee");
    sp.delete("priority");
    sp.delete("type");
    setSearchParams(sp, { replace: true });
  };

  const activeBoard: Board | null = useMemo(() => {
    if (urlBoardSlug) {
      return findBoardBySlug(config.boards, urlBoardSlug) ?? null;
    }
    return config.boards[0] ?? null;
  }, [config.boards, urlBoardSlug]);
  // Live ref so the by-key fetch can bounce to the current board without re-running on every
  // board change.
  const activeBoardRef = useRef<Board | null>(activeBoard);
  activeBoardRef.current = activeBoard;

  // Local-first hydration: own the single sync of the active board's working set. Columns read
  // the resulting in-memory store (via useColumnSource) so filter/JQL/group changes are local —
  // no per-column requests. Empty id while the board is still resolving is a no-op.
  const boardStore = useBoardStore(activeBoard?.id ?? "");

  const needsRedirectToFirst: boolean = !urlBoardSlug && activeBoard !== null;
  // Only bounce an unknown board AFTER config has loaded — during the initial load every
  // board looks "unknown" (config.boards is still empty), and bouncing would drop the URL.
  const needsBounceUnknownBoard: boolean = boardLoaded && !!urlBoardSlug && activeBoard === null;

  const boardProjects: Project[] = useMemo(() => {
    if (!activeBoard) return [];
    const keys: Set<string> = new Set(boardProjectKeys(activeBoard));
    return config.projects.filter((project: Project) => keys.has(project.key));
  }, [config.projects, activeBoard]);
  const showProjectChip: boolean = activeBoard?.scope.kind === "multi_project";

  // The drawer's issue is resolved by key on demand (no full-snapshot scan); refetch on any
  // board mutation so the drawer header stays current. The "unknown issue → bounce" happens
  // INSIDE the fetch's failure path — not a separate state-gated effect, which would read a
  // stale "resolved" flag in the same render and navigate away before the fetch even resolves.
  const [openIssue, setOpenIssue] = useState<Issue | null>(null);
  // The id whose FULL record (incl. description) has been fetched by-key. openIssue is first seeded
  // from a light card (no description); the description editor stays skeletoned until this matches
  // the open issue, so it never flashes its empty placeholder before the text arrives.
  const [hydratedIssueId, setHydratedIssueId] = useState<string | null>(null);
  // Live id of the resolved panel issue, so the change handler can re-resolve only when an
  // event actually mutates *this* issue's header — not on every board signal.
  const openIssueIdRef = useRef<string | null>(null);
  openIssueIdRef.current = openIssue?.id ?? null;
  useEffect(() => {
    if (!urlIssueKey) {
      setOpenIssue(null);
      return;
    }
    let cancelled: boolean = false;
    const load = async (): Promise<void> => {
      try {
        const found: Issue = await BoardApi.getIssueByKey(urlIssueKey);
        if (!cancelled) {
          setOpenIssue(found);
          setHydratedIssueId(found.id);
        }
      } catch {
        // Genuinely not found (deleted / wrong board) → drop the key back to the board.
        if (!cancelled) {
          setOpenIssue(null);
          const board: Board | null = activeBoardRef.current;
          if (board) navigate(buildBoardPath(domainName ?? "", board), { replace: true });
        }
      }
    };
    void load();
    const unsubscribe: () => void = BoardService.subscribe((event: BoardChangeEvent) => {
      const id: string | null = openIssueIdRef.current;
      if (id !== null && issueHeaderChanged(event, id)) void load();
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [urlIssueKey, domainName, navigate]);

  // Bounce on unknown board id so the redirect path picks the first
  useEffect(() => {
    if (needsBounceUnknownBoard) {
      navigate(`/${domainName}/board`, { replace: true });
    }
  }, [needsBounceUnknownBoard, domainName, navigate]);

  // Multi-user live sync: keep the board events SSE stream open while a board view is mounted.
  // Teammates' changes arrive over the stream and reconcile through the same scoped-invalidation
  // path (BoardService.applyRemote) as local edits — no per-view wiring needed.
  useEffect(() => {
    connectBoardEventStream();
    return () => disconnectBoardEventStream();
  }, []);

  const [filter, setFilter] = useState<string>("");
  // The text input updates `filter` on every keystroke (instant feedback), but the value
  // that drives the per-column server queries is debounced so a burst of typing fires one
  // request, not one per character. Chips/JQL stay immediate — only the free-text `q` waits.
  const [debouncedFilter, setDebouncedFilter] = useState<string>("");
  const [creatingIn, setCreatingIn] = useState<string | null>(null);
  // The column the pointer is currently over, so the "c" shortcut creates where the mouse is
  // (a ref, not state — it must not trigger re-renders on every mouse move).
  const hoveredColumnRef = useRef<string | null>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState<boolean>(false);
  const [paletteOpen, setPaletteOpen] = useState<boolean>(false);
  const aiEnabled: boolean = useBoardAiEnabled();
  const [askAiOpen, setAskAiOpen] = useState<boolean>(false);
  // The create drawer hosts an unsaved draft Issue (no id). null = closed. It's the same
  // IssuePanel/IssueDetail as viewing an issue, in isNewIssue mode.
  const [newIssueDraft, setNewIssueDraft] = useState<Issue | null>(null);
  const [importOpen, setImportOpen] = useState<boolean>(false);
  // Child issues queued on a new EPIC draft (committed once the epic is created — see createFromDraft).
  const [newEpicExisting, setNewEpicExisting] = useState<IssueCardData[]>([]);
  const [newEpicToCreate, setNewEpicToCreate] = useState<string[]>([]);
  const newEpicChildren: NewEpicChildren = useMemo(
    () => ({
      existing: newEpicExisting,
      toCreate: newEpicToCreate,
      addExisting: (card: IssueCardData) =>
        setNewEpicExisting((prev: IssueCardData[]) =>
          prev.some((c: IssueCardData) => c.id === card.id) ? prev : [...prev, card]
        ),
      removeExisting: (id: string) =>
        setNewEpicExisting((prev: IssueCardData[]) => prev.filter((c: IssueCardData) => c.id !== id)),
      queueCreate: (summary: string) => setNewEpicToCreate((prev: string[]) => [...prev, summary]),
      removeCreate: (index: number) =>
        setNewEpicToCreate((prev: string[]) => prev.filter((_, i: number) => i !== index))
    }),
    [newEpicExisting, newEpicToCreate]
  );
  const [density, setDensityState] = useState<Density>(() => getStoredDensity());
  const [announcement, setAnnouncement] = useState<string>("");
  const [archivedOpen, setArchivedOpen] = useState<boolean>(false);
  interface MenuState {
    x: number;
    y: number;
    issueId: string;
  }
  const [contextMenu, setContextMenu] = useState<MenuState | null>(null);
  interface UndoToast {
    issueId: string;
    issueKey: string;
    expiresAt: number;
  }
  const [undoToast, setUndoToast] = useState<UndoToast | null>(null);
  useEffect(() => {
    if (!undoToast) return;
    const remaining: number = undoToast.expiresAt - Date.now();
    if (remaining <= 0) {
      setUndoToast(null);
      return;
    }
    const timer: number = window.setTimeout(() => setUndoToast(null), remaining);
    return () => window.clearTimeout(timer);
  }, [undoToast]);
  const [focus, setFocus] = useState<FocusState>(() => ({ columnId: "", issueId: null }));
  // A synchronously-updated mirror of `focus`. The keydown listener (and the move/navigate
  // callbacks it captured) only refreshes after a re-render, so a fast burst — e.g. Shift+J ×3 to
  // shift a card three columns — is handled by one stale closure where focus.statusId is still the
  // starting column; every press then recomputes "one column over" and the card never advances
  // past the first hop. Reading/writing focus through this ref makes each press see the previous
  // one's result immediately. setFocusNow writes the ref synchronously AND the state.
  const focusRef = useRef<FocusState>(focus);
  focusRef.current = focus;
  const setFocusNow = useCallback((next: FocusState): void => {
    focusRef.current = next;
    setFocus(next);
  }, []);

  const setDensity = (next: Density): void => {
    setDensityState(next);
    setStoredDensity(next);
  };
  // Registry of imperatively-focused elements, keyed by name. Each is wired with a callback ref
  // (KuikaInput forwards its ref to the real <input>), so focusing reads `boardRefs.current.filter`
  // — no class-based DOM queries, and new targets just add a key.
  const boardRefs = useRef<BoardElementRefs>({ filter: null });
  // The board root element, passed to the issue drawer so its outside-click dismissal can tell a
  // board click from a portaled popup (which renders at <body>, outside this element).
  const boardRootRef = useRef<HTMLDivElement | null>(null);
  // Debounced keyboard move (Shift+J/K): the latest pending move + its flush timer.
  const pendingMoveRef = useRef<PendingMove | null>(null);
  const moveFlushTimerRef = useRef<number | null>(null);
  const goToPendingAtRef = useRef<number>(0);

  // Reset focus + creation panels when active board changes
  useEffect(() => {
    setFocus({ columnId: "", issueId: null });
    setCreatingIn(null);
  }, [activeBoard?.id, config.statuses]);

  // Opening/closing the detail drawer is a navigation within the board, so it must carry the
  // current search params (jql, filter chips, groupBy, view) — otherwise clicking an issue would
  // reset the active JQL/filters. Read live via a ref so goToIssue stays referentially stable.
  const searchRef = useRef<string>("");
  searchRef.current = searchParams.toString();

  const goToIssue = useCallback(
    (issue: IssueKeyRef | null): void => {
      if (!activeBoard) return;
      // Opening an issue and the create draft are mutually exclusive (one drawer at a time).
      if (issue) setNewIssueDraft(null);
      const base: string = buildBoardPath(domainName ?? "", activeBoard);
      const search: string = searchRef.current;
      navigate({ pathname: issue ? `${base}/${issue.key}` : base, search });
    },
    [activeBoard, domainName, navigate]
  );

  // Open the create drawer with a blank draft. Closes any open issue (one drawer at a time).
  const openNewIssue = useCallback((): void => {
    const todoStatusId: string =
      config.statuses.find((status: BoardStatus) => status.category === "todo")?.id ?? config.statuses[0]?.id ?? "";
    setOpenIssue(null);
    setNewEpicExisting([]);
    setNewEpicToCreate([]);
    setNewIssueDraft({
      id: "",
      key: "",
      projectKey: config.projects[0]?.key ?? "",
      type: "task",
      statusId: todoStatusId,
      summary: "",
      descriptionMarkdown: "",
      priority: "medium",
      assigneeUserId: null,
      reporterUserId: null,
      labels: [],
      storyPoints: null,
      startDate: null,
      dueDate: null,
      parentIssueId: null,
      epicId: null,
      sprintId: null,
      watcherUserIds: [],
      archivedAt: null,
      epicName: null,
      epicColor: null,
      originalEstimateMin: null,
      remainingEstimateMin: null,
      timeSpentMin: null,
      fixVersionId: null,
      customFields: {},
      rank: "",
      createdAt: "",
      updatedAt: ""
    });
  }, [config]);

  // Persist the draft, then apply the fields CreateIssueInput doesn't carry (description, dates,
  // estimates, story points, fix version) as a follow-up edit. Closes the drawer.
  const createFromDraft = useCallback((): void => {
    const draft: Issue | null = newIssueDraft;
    if (!draft || !draft.summary.trim() || !draft.projectKey || !draft.statusId) return;
    setNewIssueDraft(null);
    void BoardService.createIssue({
      projectKey: draft.projectKey,
      type: draft.type,
      summary: draft.summary.trim(),
      statusId: draft.statusId,
      priority: draft.priority,
      assigneeUserId: draft.assigneeUserId,
      labels: draft.labels,
      parentIssueId: draft.parentIssueId,
      epicId: draft.epicId
    }).then((created: Issue | null): void => {
      if (!created) return;
      const extra: UpdateIssueInput = {};
      if (draft.descriptionMarkdown.trim()) extra.descriptionMarkdown = draft.descriptionMarkdown;
      if (draft.storyPoints != null) extra.storyPoints = draft.storyPoints;
      if (draft.dueDate) extra.dueDate = draft.dueDate;
      if (draft.startDate) extra.startDate = draft.startDate;
      if (draft.fixVersionId) extra.fixVersionId = draft.fixVersionId;
      if (draft.originalEstimateMin != null) extra.originalEstimateMin = draft.originalEstimateMin;
      if (draft.remainingEstimateMin != null) extra.remainingEstimateMin = draft.remainingEstimateMin;
      if (draft.timeSpentMin != null) extra.timeSpentMin = draft.timeSpentMin;
      if (Object.keys(extra).length > 0) void BoardService.updateIssue(created.id, extra);
      // Commit the child issues queued on the epic draft now that the epic exists: link the existing
      // ones and create the new ones under it, then open the epic.
      if (created.type === "epic") {
        const childStatusId: string =
          config.statuses.find((s: BoardStatus) => s.projectKey === created.projectKey && s.category === "todo")?.id ??
          config.statuses.find((s: BoardStatus) => s.projectKey === created.projectKey)?.id ??
          created.statusId;
        for (const child of newEpicExisting) void BoardService.updateIssue(child.id, { epicId: created.id });
        for (const summary of newEpicToCreate) {
          const trimmed: string = summary.trim();
          if (trimmed) {
            void BoardService.createIssue({
              projectKey: created.projectKey,
              type: "task",
              summary: trimmed,
              statusId: childStatusId,
              epicId: created.id
            });
          }
        }
        setNewEpicExisting([]);
        setNewEpicToCreate([]);
        setOpenIssue(created);
      }
    });
  }, [newIssueDraft, newEpicExisting, newEpicToCreate, config.statuses]);

  // Open the drawer from a card: seed openIssue from the card so the panel appears instantly
  // (with a loading body), then navigate — the by-key fetch + detail bundle fill it in.
  const openIssueCard = useCallback(
    (card: IssueCardData): void => {
      // Re-clicking the already-open issue is a no-op: the by-key fetch is keyed off the URL, so
      // re-seeding without a URL change would strand the description skeleton (no fetch re-fires).
      if (openIssueIdRef.current === card.id) return;
      // The card carries no description, so clear the hydration marker before seeding — otherwise a
      // previously-opened issue would still read as "hydrated" (its id last set the marker) and the
      // editor would unmask its empty placeholder before the by-key fetch refills the description.
      // The fetch below re-sets the marker once the full record lands.
      setHydratedIssueId(null);
      setOpenIssue(issueFromCard(card));
      goToIssue(card);
    },
    [goToIssue]
  );

  // Live mirror of the resolved columns, so the optimistic helpers below (defined before the
  // `columns` memo) can map a card's statusId to the column that currently holds it.
  const columnsRef = useRef<ResolvedColumn[]>([]);

  // Board-view columns load their own pages from the server (a per-column useColumnIssues
  // inside <BoardColumn>); each column reports its loaded cards + total up here, keyed by COLUMN
  // id, so keyboard navigation, drag-drop and the blocked badge work against the loaded set.
  const [loadedByColumn, setLoadedByColumn] = useState<Record<string, IssueCardData[]>>({});
  const [totalByColumn, setTotalByColumn] = useState<Record<string, number>>({});
  const handleColumnData = useCallback((columnId: string, items: IssueCardData[], total: number): void => {
    setLoadedByColumn((prev: Record<string, IssueCardData[]>) =>
      prev[columnId] === items ? prev : { ...prev, [columnId]: items }
    );
    setTotalByColumn((prev: Record<string, number>) =>
      prev[columnId] === total ? prev : { ...prev, [columnId]: total }
    );
  }, []);

  // Each column registers its optimistic move primitives here (keyed by column id) so a drag-drop
  // can move the dragged card across columns instantly (no snap-back) before the reload reconciles.
  const columnApi = useRef<Map<string, ColumnApi>>(new Map());
  const registerColumnApi = useCallback((columnId: string, api: ColumnApi): (() => void) => {
    columnApi.current.set(columnId, api);
    return () => {
      columnApi.current.delete(columnId);
    };
  }, []);

  // Resolve the column currently holding a given status, then act on its registered API. Optimistic
  // helpers for card actions (context menu / keyboard): reflect the change in the column instantly;
  // the mutation's reload reconciles to server truth.
  const apiForStatus = useCallback((statusId: string): ColumnApi | undefined => {
    const columnId: string | null = columnIdForStatus(columnsRef.current, statusId);
    return columnId ? columnApi.current.get(columnId) : undefined;
  }, []);
  const optimisticPatch = useCallback(
    (card: IssueCardData, patch: Partial<IssueCardData>): void => {
      apiForStatus(card.statusId)?.patchOptimistic(card.id, patch);
    },
    [apiForStatus]
  );
  const optimisticRemove = useCallback(
    (card: IssueCardData): void => {
      apiForStatus(card.statusId)?.removeOptimistic(card.id);
    },
    [apiForStatus]
  );
  // sourceStatusId is passed explicitly (not read from card.statusId) because during a rapid
  // keyboard burst the `card` snapshot is stale — the live source status is the caller's current
  // focus. Removing from the wrong column would leave the card duplicated across columns. Both
  // source and destination statuses are mapped to their holding columns.
  const optimisticMove = useCallback(
    (card: IssueCardData, sourceStatusId: string, destStatusId: string, index: number): void => {
      apiForStatus(sourceStatusId)?.removeOptimistic(card.id);
      apiForStatus(destStatusId)?.insertOptimistic({ ...card, statusId: destStatusId }, index);
    },
    [apiForStatus]
  );

  // Persist the pending keyboard move (if any) now, cancelling its timer. A net no-op (card moved
  // out and back to its origin) is dropped — the optimistic UI already settled, so there's nothing
  // to write. The board's move event fires once here, not per keypress.
  const flushPendingMove = useCallback((): void => {
    if (moveFlushTimerRef.current !== null) {
      window.clearTimeout(moveFlushTimerRef.current);
      moveFlushTimerRef.current = null;
    }
    const pending: PendingMove | null = pendingMoveRef.current;
    pendingMoveRef.current = null;
    if (!pending || pending.destinationStatusId === pending.sourceStatusId) return;
    void BoardService.moveIssue({
      issueId: pending.issueId,
      destinationStatusId: pending.destinationStatusId,
      destinationIndex: pending.destinationIndex,
      sourceStatusId: pending.sourceStatusId
    });
  }, []);

  // Queue a keyboard move, coalescing consecutive moves of the SAME issue (preserving the burst's
  // original source). Switching to a different issue flushes the previous one first.
  const scheduleMove = useCallback(
    (move: PendingMove): void => {
      const existing: PendingMove | null = pendingMoveRef.current;
      if (existing && existing.issueId !== move.issueId) {
        flushPendingMove();
      }
      const sameIssue: boolean = pendingMoveRef.current?.issueId === move.issueId;
      const sourceStatusId: string = sameIssue ? pendingMoveRef.current!.sourceStatusId : move.sourceStatusId;
      pendingMoveRef.current = { ...move, sourceStatusId };
      if (moveFlushTimerRef.current !== null) {
        window.clearTimeout(moveFlushTimerRef.current);
      }
      moveFlushTimerRef.current = window.setTimeout(flushPendingMove, MOVE_DEBOUNCE_MS);
    },
    [flushPendingMove]
  );

  // Persist any pending keyboard move when the board unmounts so it's never lost.
  useEffect(() => {
    return () => flushPendingMove();
  }, [flushPendingMove]);

  // Debounce the free-text filter (~250ms) before it reaches the column queries.
  useEffect(() => {
    const trimmed: string = filter.trim();
    if (trimmed === debouncedFilter) return;
    const handle: ReturnType<typeof setTimeout> = setTimeout(() => setDebouncedFilter(trimmed), 250);
    return () => clearTimeout(handle);
  }, [filter, debouncedFilter]);

  // Filters pushed to every column query — all evaluated server-side.
  const columnFilters: ColumnFilters = useMemo(
    () => ({
      q: debouncedFilter || undefined,
      jql: jqlInput.trim() || undefined,
      assignee: filters.assignee,
      priority: filters.priority,
      type: filters.type
    }),
    [debouncedFilter, jqlInput, filters]
  );

  // The board's rendered columns (named lanes, each mapping to a set of statuses).
  const columns: ResolvedColumn[] = useMemo(
    () => (activeBoard ? boardColumns(activeBoard, config.statuses) : []),
    [activeBoard, config.statuses]
  );
  columnsRef.current = columns;

  // ── Suggested lane ───────────────────────────────────────────────────────────────────────
  // Derived from cards the board has ALREADY loaded — no extra request, no model call. Dismissals
  // and the lane's visibility are personal (localStorage), because "not a problem for me right
  // now" is a per-person judgement, not board state to broadcast.
  const [dismissedSuggestions, setDismissedSuggestionsState] = useState<string[]>([]);
  const [suggestionsHidden, setSuggestionsHiddenState] = useState<boolean>(false);
  // Both are per BOARD: switching boards has to bring that board's own dismissals and its own
  // hide flag, not the previous board's.
  useEffect(() => {
    setDismissedSuggestionsState(activeBoard ? getDismissedSuggestions(activeBoard.id) : []);
    setSuggestionsHiddenState(activeBoard ? getSuggestionsHidden(activeBoard.id) : false);
  }, [activeBoard]);

  const suggestions: Suggestion[] = useMemo(() => {
    if (suggestionsHidden) return [];
    const cards: IssueCardData[] = Object.values(loadedByColumn).flat();
    if (cards.length === 0) return [];
    const categoryByStatus: Map<string, StatusCategory> = new Map();
    for (const column of columns) {
      for (const status of column.statuses) categoryByStatus.set(status.id, status.category);
    }
    return buildSuggestions({
      cards,
      categoryOf: (statusId: string): StatusCategory => categoryByStatus.get(statusId) ?? "todo",
      dismissedIds: dismissedSuggestions
    });
  }, [suggestionsHidden, loadedByColumn, columns, dismissedSuggestions]);

  // Prune ids that no longer name a live card, so the dismissal list cannot grow forever.
  useEffect(() => {
    if (!activeBoard || dismissedSuggestions.length === 0) return;
    const cards: IssueCardData[] = Object.values(loadedByColumn).flat();
    if (cards.length === 0) return;
    const pruned: string[] = pruneDismissedSuggestions(dismissedSuggestions, cards);
    if (pruned.length === dismissedSuggestions.length) return;
    setDismissedSuggestionsState(pruned);
    setDismissedSuggestions(activeBoard.id, pruned);
  }, [activeBoard, dismissedSuggestions, loadedByColumn]);

  const dismissSuggestion = useCallback(
    (card: IssueCardData): void => {
      if (!activeBoard) return;
      const next: string[] = [...dismissedSuggestions, card.id];
      setDismissedSuggestionsState(next);
      setDismissedSuggestions(activeBoard.id, next);
    },
    [activeBoard, dismissedSuggestions]
  );

  const assignSuggestionToMe = useCallback(
    (card: IssueCardData): void => {
      // Same pair the context menu's "Assign to me" uses: reflect it now, let the reload reconcile.
      optimisticPatch(card, { assigneeUserId: currentUserId() });
      BoardService.updateIssue(card.id, { assigneeUserId: currentUserId() });
    },
    [optimisticPatch]
  );

  const hideSuggestionLane = useCallback((): void => {
    if (!activeBoard) return;
    setSuggestionsHiddenState(true);
    setSuggestionsHidden(activeBoard.id, true);
  }, [activeBoard]);

  // The board's epics (loaded separately — epics aren't board cards). Powers group-by-epic labels
  // and the Epics filter. Refetched on any issue change so a newly-created/edited epic shows up.
  const [epics, setEpics] = useState<EpicCard[]>([]);
  useEffect(() => {
    const boardId: string | undefined = activeBoard?.id;
    if (!boardId) {
      setEpics([]);
      return;
    }
    let cancelled: boolean = false;
    const load = (): void => {
      void BoardApi.getEpics(boardId)
        .then((list: EpicCard[]) => {
          if (!cancelled) setEpics(list);
        })
        .catch(() => undefined);
    };
    load();
    const unsubscribe: () => void = BoardService.subscribe((event: BoardChangeEvent) => {
      if (event.kind.startsWith("issue.")) load();
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [activeBoard?.id]);

  // Resolve an epic's display label + colour from the board's epic list (epic grouping / chips).
  const epicById = useMemo(() => {
    const map: Map<string, { label: string; color?: string }> = new Map();
    for (const entry of epics) {
      map.set(entry.epic.id, {
        label: `${entry.epic.key}  ${entry.epic.epicName ?? entry.epic.summary}`,
        color: entry.epic.epicColor ?? "var(--purple-5)"
      });
    }
    return map;
  }, [epics]);
  const epicLabel = useCallback(
    (epicId: string): { label: string; color?: string } | undefined => epicById.get(epicId),
    [epicById]
  );

  const findLoadedCard = useCallback(
    (issueId: string): IssueCardData | undefined => {
      for (const columnId of Object.keys(loadedByColumn)) {
        const found: IssueCardData | undefined = loadedByColumn[columnId]?.find(
          (card: IssueCardData) => card.id === issueId
        );
        if (found) return found;
      }
      return undefined;
    },
    [loadedByColumn]
  );

  // Assignee filter options come from the workspace member directory (no full-issue scan).
  const [members, setMembers] = useState<DirectoryMember[]>(() => getMembers());
  useEffect(() => {
    const unsubscribe: () => void = subscribePeople(() => setMembers(getMembers()));
    void loadPeopleDirectory();
    return unsubscribe;
  }, []);
  const assigneeOptions: DirectoryMember[] = members;

  // Enumerable values the JQL bar offers as autocomplete, sourced from the live board config.
  const jqlContext: JqlSuggestContext = useMemo(
    () => ({
      priorities: PRIORITY_ORDER,
      types: Object.keys(ISSUE_TYPE_LABELS),
      statuses: config.statuses.map((status: BoardStatus) => ({ id: status.id, name: status.name })),
      assignees: members.map((member: DirectoryMember) => ({ id: member.id, name: member.name })),
      labels: config.labels.map((label: LabelDef) => label.name),
      projects: boardProjects.map((project: Project) => project.key),
      currentUser: currentUserId()
    }),
    [config.statuses, config.labels, members, boardProjects]
  );

  // Blocked badge comes from the server-computed flag on each loaded card.
  const blockedIssueIds: Set<string> = useMemo(() => {
    const result: Set<string> = new Set<string>();
    for (const columnId of Object.keys(loadedByColumn)) {
      for (const card of loadedByColumn[columnId] ?? []) {
        if (card.isBlocked) result.add(card.id);
      }
    }
    return result;
  }, [loadedByColumn]);

  // §17: warn before moving a blocked issue into an in-progress column.
  // Returns false if the user cancels the transition.
  const confirmBlockedTransition = useCallback(
    (issueId: string, destinationStatusId: string): boolean => {
      if (!blockedIssueIds.has(issueId)) return true;
      const destCategory: string | undefined = config.statuses.find(
        (status: BoardStatus) => status.id === destinationStatusId
      )?.category;
      if (destCategory !== "in_progress") return true;
      return window.confirm("This issue is blocked by an unresolved dependency. Move it to In Progress anyway?");
    },
    [blockedIssueIds, config.statuses]
  );

  const moveFocus = useCallback(
    (direction: "left" | "right" | "up" | "down"): void => {
      const current: FocusState = focusRef.current;
      const cols: ResolvedColumn[] = columns;
      if (cols.length === 0) return;
      const columnIndex: number = Math.max(
        0,
        cols.findIndex((column: ResolvedColumn) => column.id === current.columnId)
      );
      if (direction === "left" || direction === "right") {
        const nextIndex: number =
          direction === "right" ? Math.min(cols.length - 1, columnIndex + 1) : Math.max(0, columnIndex - 1);
        const targetColumn: ResolvedColumn = cols[nextIndex]!;
        const targetIssues: IssueCardData[] = loadedByColumn[targetColumn.id] ?? [];
        setFocusNow({ columnId: targetColumn.id, issueId: targetIssues[0]?.id ?? null });
        return;
      }
      const currentIssues: IssueCardData[] = loadedByColumn[current.columnId] ?? [];
      if (currentIssues.length === 0) return;
      const currentPos: number = current.issueId
        ? currentIssues.findIndex((card: IssueCardData) => card.id === current.issueId)
        : -1;
      const nextPos: number =
        direction === "down" ? Math.min(currentIssues.length - 1, currentPos + 1) : Math.max(0, currentPos - 1);
      const target: IssueCardData | undefined = currentIssues[nextPos];
      if (target) setFocusNow({ columnId: current.columnId, issueId: target.id });
    },
    [columns, loadedByColumn, setFocusNow]
  );

  const moveFocusedIssue = useCallback(
    (direction: "left" | "right"): void => {
      const current: FocusState = focusRef.current;
      if (!current.issueId) return;
      const cols: ResolvedColumn[] = columns;
      const columnIndex: number = cols.findIndex((column: ResolvedColumn) => column.id === current.columnId);
      if (columnIndex < 0) return;
      const nextIndex: number =
        direction === "right" ? Math.min(cols.length - 1, columnIndex + 1) : Math.max(0, columnIndex - 1);
      if (nextIndex === columnIndex) return;
      const destColumn: ResolvedColumn = cols[nextIndex]!;
      const card: IssueCardData | undefined = findLoadedCard(current.issueId);
      if (!card) return;
      // A column maps to a set of statuses; resolve which concrete status the card lands in.
      const destinationStatusId: string | null = resolveDropStatus(destColumn, card.statusId);
      if (destinationStatusId === null) return;
      const kbProject: Project | undefined = config.projects.find((p: Project) => p.key === card.projectKey);
      if (!isEdgeAllowed(kbProject, card.statusId, destinationStatusId)) {
        toastService.warning("That move isn't allowed by this project's workflow.");
        return;
      }
      if (!confirmBlockedTransition(current.issueId, destinationStatusId)) return;
      const destinationIndex: number = (loadedByColumn[destColumn.id] ?? []).length;
      optimisticMove(card, card.statusId, destinationStatusId, destinationIndex);
      // Optimistic move is instant; the backend write is debounced so a multi-column burst is one
      // request to the final status (scheduleMove preserves the burst's original source).
      scheduleMove({
        issueId: current.issueId,
        destinationStatusId,
        destinationIndex,
        sourceStatusId: card.statusId
      });
      setFocusNow({ columnId: destColumn.id, issueId: current.issueId });
    },
    [
      columns,
      loadedByColumn,
      config.projects,
      confirmBlockedTransition,
      findLoadedCard,
      optimisticMove,
      scheduleMove,
      setFocusNow
    ]
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      // Cmd/Ctrl-K opens palette regardless of focus target
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k" && !event.shiftKey) {
        event.preventDefault();
        setPaletteOpen(true);
        return;
      }
      // Cmd/Ctrl-B cycles to the next board
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "b" && !event.shiftKey && activeBoard) {
        event.preventDefault();
        const index: number = config.boards.findIndex((board: Board) => board.id === activeBoard.id);
        const next: Board = config.boards[(index + 1) % config.boards.length]!;
        navigate(buildBoardPath(domainName ?? "", next));
        return;
      }
      // Cmd/Ctrl-. focuses filter input
      if ((event.metaKey || event.ctrlKey) && event.key === ".") {
        event.preventDefault();
        boardRefs.current.filter?.focus();
        return;
      }
      if (isEditableTarget(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      // Normalize single-character keys to lowercase so letter shortcuts work regardless of Shift
      // (event.key is "J" when Shift is held) or Caps Lock. Multi-char keys ("Enter", "Escape", …)
      // pass through unchanged. Shift itself is still read via event.shiftKey below.
      const key: string = event.key.length === 1 ? event.key.toLowerCase() : event.key;

      // "g" then b/l/r — Linear-style go-to sequences (board / backlog / roadmap)
      const now: number = Date.now();
      if (goToPendingAtRef.current && now - goToPendingAtRef.current < 800) {
        goToPendingAtRef.current = 0;
        if (key === "b" || key === "l" || key === "r") {
          event.preventDefault();
          let target: BoardView = "board";
          if (key === "l") target = "backlog";
          else if (key === "r") target = "roadmap";
          setView(target);
          return;
        }
      }
      if (key === "g") {
        goToPendingAtRef.current = now;
        return;
      }

      switch (key) {
        case "?":
          event.preventDefault();
          setShortcutsOpen((prev: boolean) => !prev);
          return;
        case "c":
          if (!openIssue) {
            event.preventDefault();
            // Create in the column under the pointer; fall back to the focused/first column when
            // the mouse isn't over any column (e.g. focus-driven keyboard use).
            const createIn: string | null =
              hoveredColumnRef.current ?? (focus.columnId || null) ?? columns[0]?.id ?? null;
            if (createIn) {
              setFocus({ columnId: createIn, issueId: focus.issueId });
              setCreatingIn(createIn);
            }
          }
          return;
        case "/":
          event.preventDefault();
          boardRefs.current.filter?.focus();
          return;
        case "j":
          event.preventDefault();
          if (event.shiftKey) moveFocusedIssue("right");
          else moveFocus("down");
          return;
        case "k":
          event.preventDefault();
          if (event.shiftKey) moveFocusedIssue("left");
          else moveFocus("up");
          return;
        case "h":
          event.preventDefault();
          moveFocus("left");
          return;
        case "l":
          event.preventDefault();
          moveFocus("right");
          return;
        case "Enter":
          if (focus.issueId) {
            event.preventDefault();
            const card: IssueCardData | undefined = findLoadedCard(focus.issueId);
            if (card) openIssueCard(card);
          }
          return;
        case "a":
          if (focus.issueId && !openIssue) {
            event.preventDefault();
            const card: IssueCardData | undefined = findLoadedCard(focus.issueId);
            if (card) optimisticPatch(card, { assigneeUserId: currentUserId() });
            BoardService.updateIssue(focus.issueId, { assigneeUserId: currentUserId() });
          }
          return;
        case "x":
          if (focus.issueId && !openIssue) {
            event.preventDefault();
            const target: IssueCardData | undefined = findLoadedCard(focus.issueId);
            if (target) {
              optimisticRemove(target);
              BoardService.archiveIssue(target.id);
              setUndoToast({ issueId: target.id, issueKey: target.key, expiresAt: Date.now() + 6000 });
            }
          }
          return;
        case "w":
          if (focus.issueId && !openIssue) {
            event.preventDefault();
            BoardService.toggleWatch(focus.issueId);
          }
          return;
        case "1":
        case "2":
        case "3":
        case "4":
        case "5":
          if (focus.issueId && !openIssue) {
            event.preventDefault();
            const order: IssuePriority[] = ["urgent", "high", "medium", "low", "none"];
            const next: IssuePriority | undefined = order[Number(key) - 1];
            if (next) {
              const card: IssueCardData | undefined = findLoadedCard(focus.issueId);
              if (card) optimisticPatch(card, { priority: next });
              BoardService.updateIssue(focus.issueId, { priority: next });
            }
          }
          return;
        case "Escape":
          if (creatingIn) {
            event.preventDefault();
            setCreatingIn(null);
          } else if (openIssue) {
            event.preventDefault();
            goToIssue(null);
          }
          return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    focus,
    openIssue,
    creatingIn,
    config,
    columns,
    moveFocus,
    moveFocusedIssue,
    goToIssue,
    openIssueCard,
    setView,
    findLoadedCard,
    optimisticPatch,
    optimisticRemove
  ]);

  if (needsRedirectToFirst && activeBoard) {
    return <Navigate to={buildBoardPath(domainName ?? "", activeBoard)} replace />;
  }

  if (!activeBoard) {
    // Board-page only mounts under a board route now (the projects index + project hub own
    // onboarding/enablement). An unresolved board shows a skeleton while config loads, then
    // bounces to the projects index — an unknown code never sticks.
    if (boardLoaded) return <Navigate to={`/${domainName}/board`} replace />;
    return (
      <div className="kuika-board" data-testid="kuika-board">
        <BoardLoading />
      </div>
    );
  }

  const handleDragEnd = (result: DropResult): void => {
    if (!result.destination) return;
    // Persist any debounced keyboard move first, so it can't fire after this drag and clobber it.
    flushPendingMove();
    if (result.type === "column") {
      if (result.source.index === result.destination.index) return;
      // Reorder the rendered columns and persist the whole column array (ids preserved). This also
      // promotes an implicit (one-per-status) layout to explicit columns on first reorder.
      const reordered: ResolvedColumn[] = [...columns];
      const [moved] = reordered.splice(result.source.index, 1);
      if (!moved) return;
      reordered.splice(result.destination.index, 0, moved);
      const payload: BoardColumnDef[] = reordered.map((column: ResolvedColumn, index: number) => ({
        id: column.id,
        name: column.name,
        statusIds: column.statusIds,
        orderIndex: index
      }));
      void BoardService.setBoardColumns(activeBoard.id, payload);
      setAnnouncement(`Reordered column ${moved.name} to position ${result.destination.index + 1}`);
      return;
    }
    const sourceColumnId: string = result.source.droppableId;
    const destColumnId: string = result.destination.droppableId;
    if (sourceColumnId === destColumnId && result.source.index === result.destination.index) {
      return;
    }
    const destColumn: ResolvedColumn | undefined = columns.find((column: ResolvedColumn) => column.id === destColumnId);
    if (!destColumn) return;
    const movedIssue: IssueCardData | undefined = findLoadedCard(result.draggableId);
    // Resolve which concrete status the card lands in: keep its current one if the destination
    // column already includes it (reorder within the lane), else the column's first status.
    const destStatus: string | null = movedIssue
      ? resolveDropStatus(destColumn, movedIssue.statusId)
      : (destColumn.statusIds[0] ?? null);
    if (destStatus === null) return;
    if (movedIssue) {
      const dragProject: Project | undefined = config.projects.find((p: Project) => p.key === movedIssue.projectKey);
      if (!isEdgeAllowed(dragProject, movedIssue.statusId, destStatus)) {
        toastService.warning("That move isn't allowed by this project's workflow.");
        return;
      }
    }
    if (!confirmBlockedTransition(result.draggableId, destStatus)) return;
    // Optimistically move the card so it stays at the drop position instead of snapping back
    // to its origin while the request is in flight; the move's reload reconciles to truth.
    if (movedIssue) {
      columnApi.current.get(sourceColumnId)?.removeOptimistic(movedIssue.id);
      columnApi.current
        .get(destColumnId)
        ?.insertOptimistic({ ...movedIssue, statusId: destStatus }, result.destination.index);
    }
    BoardService.moveIssue({
      issueId: result.draggableId,
      destinationStatusId: destStatus,
      destinationIndex: result.destination.index,
      sourceStatusId: movedIssue?.statusId ?? destStatus
    });
    setFocus({ columnId: destColumnId, issueId: result.draggableId });
    setAnnouncement(
      `Moved ${movedIssue?.key ?? "issue"} to ${destColumn.name}, position ${result.destination.index + 1}`
    );
  };

  const inlineCreateProjectDefault: string =
    activeBoard.scope.kind === "project" ? activeBoard.scope.projectKey : (boardProjects[0]?.key ?? "");

  const subtitle: React.ReactNode = (() => {
    if (activeBoard.scope.kind === "project") {
      const project: Project | undefined = boardProjects[0];
      return (
        <>
          <span
            className="kuika-board__subtitle-dot"
            style={{ background: project?.color ?? "var(--dark-4)" }}
            aria-hidden="true"
          />
          <span className="kuika-board__subtitle-key">{project?.key}</span>
          <span className="kuika-board__subtitle-divider" />
          <span>{project?.name}</span>
        </>
      );
    }
    return (
      <>
        <span className="kuika-board__subtitle-pill">Multi</span>
        <span>{boardProjects.length} projects</span>
        <span className="kuika-board__subtitle-divider" />
        <span className="kuika-board__subtitle-key">
          {boardProjects.map((entry: Project) => entry.key).join(" · ")}
        </span>
      </>
    );
  })();

  const viewTabs: { id: BoardView; label: string; icon: React.ReactNode }[] = [
    { id: "board", label: "Board", icon: <KuikaSvg name="layout-board" width={14} height={14} /> },
    { id: "list", label: "List", icon: <KuikaSvg name="list-tree" type="sidebar-icons" width={14} height={14} /> },
    { id: "backlog", label: "Backlog", icon: <KuikaSvg name="layout-rows" width={14} height={14} /> },
    { id: "roadmap", label: "Roadmap", icon: <KuikaSvg name="arrows-horizontal" width={14} height={14} /> },
    { id: "reports", label: "Reports", icon: <KuikaSvg name="report-money" width={14} height={14} /> }
  ];

  const moreMenuOptions: KuikaDropdownMenuItemType[] = [
    {
      key: "palette",
      group: 0,
      label: "Command palette",
      icon: <KuikaSvg name="search" width={15} height={15} />,
      onClick: () => setPaletteOpen(true)
    },
    {
      key: "density",
      group: 0,
      label: density === "compact" ? "Comfortable density" : "Compact density",
      icon: <KuikaSvg name={density === "compact" ? "layout-rows" : "menu-2"} width={15} height={15} />,
      onClick: () => setDensity(density === "compact" ? "comfortable" : "compact")
    },
    // Only when it is off — the lane carries its own dismiss control, so an always-present
    // "hide" duplicate in the menu would be a second way to do the same thing.
    ...(suggestionsHidden
      ? [
          {
            key: "suggestions",
            group: 0,
            label: "Show suggestions",
            icon: <KuikaSvg name="bulb" width={15} height={15} />,
            onClick: () => {
              setSuggestionsHiddenState(false);
              setSuggestionsHidden(activeBoard.id, false);
            }
          }
        ]
      : []),
    {
      key: "shortcuts",
      group: 0,
      label: "Keyboard shortcuts",
      icon: <KuikaSvg name="keyboard" width={15} height={15} />,
      onClick: () => setShortcutsOpen(true)
    },
    {
      key: "save-view",
      group: 0,
      label: "Save current view",
      icon: <KuikaSvg name="star" width={15} height={15} />,
      onClick: () => {
        const defaultName: string = `${activeBoard.name} · ${view.charAt(0).toUpperCase()}${view.slice(1)}`;
        void promptDialog({
          title: "Save view",
          label: "View name",
          defaultValue: defaultName,
          confirmLabel: "Save"
        }).then((name: string | null) => {
          if (!name) return;
          const search: string = searchParams.toString();
          const href: string = `${buildBoardPath(domainName ?? "", activeBoard)}${search ? `?${search}` : ""}`;
          SavedViews.create(name, href);
        });
      }
    },
    {
      key: "archived",
      group: 1,
      label: "Archived issues",
      icon: <KuikaSvg name="archive" width={15} height={15} />,
      onClick: () => setArchivedOpen(true)
    },
    // Workflow + Board settings are navigation to read-only-viewable pages — shown to everyone
    // (the pages enforce read-only). Import is a manager-only action.
    {
      key: "workflow",
      group: 1,
      label: "Workflow",
      icon: <KuikaSvg name="list-tree" type="sidebar-icons" width={15} height={15} />,
      // Statuses are project-owned — edit them in the project hub's Workflow tab.
      onClick: () => navigate(`/${domainName}/board/${boardProjectKeys(activeBoard)[0] ?? ""}?tab=workflow`)
    },
    {
      key: "automations",
      group: 1,
      label: "Automations",
      icon: <KuikaSvg name="automation" width={15} height={15} />,
      // Rules are project-scoped — manage them in the project hub's Automations tab.
      onClick: () => navigate(`/${domainName}/board/${boardProjectKeys(activeBoard)[0] ?? ""}?tab=automations`)
    },
    {
      key: "settings",
      group: 1,
      label: "Board settings",
      icon: <KuikaSvg name="settings" type="sidebar-icons" width={15} height={15} />,
      onClick: () => setView("settings")
    },
    ...(config.canManage
      ? [
          {
            key: "import",
            group: 1,
            label: "Import issues",
            icon: <KuikaSvg name="file-import" width={15} height={15} />,
            onClick: () => setImportOpen(true)
          }
        ]
      : [])
  ];

  return (
    <div
      ref={boardRootRef}
      className={classNames("kuika-board", `kuika-board--density-${density}`)}
      data-testid="kuika-board"
      role="application"
      aria-label={`${activeBoard.name} board`}
    >
      <span className="kuika-board__sr-only" role="status" aria-live="polite">
        {announcement}
      </span>
      <div className="kuika-board__toolbar">
        <div className="kuika-board__title-block">
          <span className="kuika-board__title">{activeBoard.name}</span>
          <span className="kuika-board__subtitle">
            {subtitle}
            <span className="kuika-board__subtitle-divider" />
            <span className="kuika-board__subtitle-pill">
              {(activeBoard.mode ?? "scrum") === "scrum" ? "Scrum" : "Kanban"}
            </span>
          </span>
        </div>

        <div className="kuika-board__view-tabs" role="tablist">
          {viewTabs.map((tab: { id: BoardView; label: string; icon: React.ReactNode }) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={view === tab.id}
              className={classNames("kuika-board__view-tab", { "kuika-board__view-tab--active": view === tab.id })}
              onClick={() => setView(tab.id)}
              data-testid={`board-view-${tab.id}`}
            >
              {view === tab.id && (
                <motion.span
                  layoutId="board-view-active-pill"
                  className="kuika-board__view-tab-pill"
                  transition={{ type: "spring", stiffness: 480, damping: 38 }}
                />
              )}
              {tab.icon}
              <span className="kuika-board__view-tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="kuika-board__toolbar-actions">
          {/* Discoverable entry to the command palette (also ⌘K). Distinct from the in-view filter. */}
          <KuikaTooltip content="Search issues & run commands" position="bottom">
            <button
              type="button"
              className="kuika-board__palette-trigger"
              onClick={() => setPaletteOpen(true)}
              aria-label="Open the command palette"
            >
              <KuikaSvg name="search" width={14} height={14} />
              <span className="kuika-board__palette-trigger-label">Search</span>
              <kbd className="kuika-board__palette-trigger-kbd">⌘K</kbd>
            </button>
          </KuikaTooltip>
          <div className="kuika-board__toolbar-filter">
            <KuikaInput
              ref={(element: HTMLInputElement | HTMLTextAreaElement | null) => {
                boardRefs.current.filter = element;
              }}
              size="sm"
              value={filter}
              placeholder="Filter…  /"
              leftIcon={<KuikaSvg name="search" width={14} height={14} />}
              onChange={(_event, value: string | number) => setFilter(String(value))}
            />
          </div>
          <NotificationsPanel />
          {aiEnabled && (
            <KuikaTooltip content="Ask the board" position="bottom">
              <KuikaButton
                type="tertiary"
                color="neutral"
                size="sm"
                isIcon
                onClick={() => setAskAiOpen(true)}
                data-testid="board-ask-ai"
              >
                <AiSparkleIcon size={16} />
              </KuikaButton>
            </KuikaTooltip>
          )}
          <KuikaDropdownMenu
            options={moreMenuOptions}
            placement="bottomRight"
            overlayClassName="kuika-board__more-menu"
          >
            <KuikaTooltip content="More" position="bottom">
              <KuikaButton type="tertiary" color="neutral" size="sm" isIcon data-testid="board-more-menu">
                <KuikaSvg name="dots" width={16} height={16} />
              </KuikaButton>
            </KuikaTooltip>
          </KuikaDropdownMenu>
          <span className="kuika-board__toolbar-divider" />
          <KuikaButton type="primary" size="sm" onClick={openNewIssue} data-testid="board-new-issue">
            <KuikaSvg name="plus" width={14} height={14} />
            <span style={{ marginLeft: 6 }}>New</span>
          </KuikaButton>
        </div>
      </div>

      {!isAdminView && (
        <>
          <FilterChips
            filters={filters}
            assigneeOptions={assigneeOptions}
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            onChange={setFilters}
            onClear={clearFilters}
          />

          <JqlBar
            initialValue={jqlInput}
            onCommit={setJqlInput}
            context={jqlContext}
            busy={boardStore.status === "syncing"}
          />
        </>
      )}

      <motion.div
        key={view}
        className="kuika-board__view-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
      >
        {view === "list" ? (
          <BoardListView
            board={activeBoard}
            boardId={activeBoard.id}
            filters={columnFilters}
            statuses={config.statuses}
            projects={config.projects}
            showProjectColumn={showProjectChip}
          />
        ) : view === "backlog" ? (
          <BoardBacklogView
            board={activeBoard}
            boardId={activeBoard.id}
            filters={columnFilters}
            sprints={config.sprints}
            statuses={config.statuses}
            projects={config.projects}
            canManage={config.canManage}
          />
        ) : view === "roadmap" ? (
          <BoardRoadmapView board={activeBoard} filter={filter} />
        ) : view === "reports" ? (
          <BoardReportsView board={activeBoard} />
        ) : view === "settings" ? (
          // Board settings render read-only for non-managers (visible-but-locked), like Releases —
          // never a vanishing page.
          <BoardSettingsPage
            board={activeBoard}
            onExit={() => setView("board")}
            onDeleted={() => navigate(`/${domainName}/board`)}
          />
        ) : (
          // Always render the columns grid — even with zero issues — so each empty column
          // shows its own "Nothing here yet" and the toolbar/column "New" can mount the
          // inline-create. (A board-level empty card here would have nowhere to host it.)
          <div className="kuika-board__columns-scroll">
            {/*
              The Suggested lane lives OUTSIDE the DragDropContext on purpose: it holds no
              status, nothing is dropped into it, and putting a non-draggable child inside the
              horizontal column Droppable would put it in rbd's index space for no benefit.
            */}
            <div className="kuika-board__board-row">
              <SuggestedLane
                suggestions={suggestions}
                onOpen={openIssueCard}
                onAssignToMe={assignSuggestionToMe}
                onDismiss={dismissSuggestion}
                onHideLane={hideSuggestionLane}
              />
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="kuika-board-columns" direction="horizontal" type="column">
                  {(columnsProvided: DroppableProvided) => (
                    <div
                      ref={columnsProvided.innerRef}
                      {...columnsProvided.droppableProps}
                      className="kuika-board__columns"
                      onMouseLeave={() => {
                        hoveredColumnRef.current = null;
                      }}
                    >
                      {columns.map((column: ResolvedColumn, columnIndex: number) => (
                        <BoardColumn
                          key={column.id}
                          boardId={activeBoard.id}
                          canManage={config.canManage}
                          column={column}
                          columnIndex={columnIndex}
                          filters={columnFilters}
                          groupBy={groupBy as ColumnGroupBy}
                          wipLimit={activeBoard.wipLimits?.[column.id]}
                          focusedIssueId={focus.columnId === column.id ? focus.issueId : null}
                          isColumnFocused={focus.columnId === column.id}
                          showProjectChip={showProjectChip}
                          projects={config.projects}
                          sprints={config.sprints}
                          labelDefs={config.labels}
                          boardProjects={boardProjects}
                          inlineCreateDefaultProjectKey={inlineCreateProjectDefault}
                          creating={creatingIn === column.id}
                          epicLabel={epicLabel}
                          registerApi={registerColumnApi}
                          onData={handleColumnData}
                          onCardClick={(card: BoardCardIssue) => {
                            setFocus({ columnId: column.id, issueId: card.id });
                            const full: IssueCardData | undefined = findLoadedCard(card.id);
                            if (full) openIssueCard(full);
                            else goToIssue(card);
                          }}
                          onCardContextMenu={(card: BoardCardIssue, x: number, y: number) => {
                            setFocus({ columnId: column.id, issueId: card.id });
                            setContextMenu({ x, y, issueId: card.id });
                          }}
                          onAddClick={() => {
                            setFocus({ columnId: column.id, issueId: focus.issueId });
                            setCreatingIn(column.id);
                          }}
                          onSetWip={() => {
                            const limit: number | null | undefined = activeBoard.wipLimits?.[column.id];
                            void promptDialog({
                              title: `WIP limit · ${column.name}`,
                              label: "Limit (blank to clear)",
                              placeholder: "e.g. 5",
                              defaultValue: limit != null ? String(limit) : "",
                              confirmLabel: "Set",
                              allowEmpty: true,
                              intent: "edit",
                              iconName: "edit"
                            }).then((raw: string | null) => {
                              if (raw === null) return;
                              const trimmed: string = raw.trim();
                              const parsed: number = Number(trimmed);
                              BoardService.setWipLimit(
                                activeBoard.id,
                                column.id,
                                trimmed === "" || Number.isNaN(parsed) || parsed <= 0 ? null : parsed
                              );
                            });
                          }}
                          onCreateSubmit={(summary: string, projectKey: string) => {
                            const createStatusId: string | undefined = column.statusIds[0];
                            if (!createStatusId) return;
                            BoardService.createIssue({
                              projectKey,
                              type: "task",
                              summary,
                              statusId: createStatusId,
                              priority: "medium"
                            });
                          }}
                          onCreateCancel={() => setCreatingIn(null)}
                          onHover={() => {
                            hoveredColumnRef.current = column.id;
                          }}
                        />
                      ))}
                      {columnsProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
        )}
      </motion.div>

      {openIssue && (
        <IssuePanel
          issue={openIssue}
          statuses={config.statuses.filter((status: BoardStatus) => status.projectKey === openIssue.projectKey)}
          project={config.projects.find((entry: Project) => entry.key === openIssue.projectKey)}
          shareUrl={`${window.location.origin}/${domainName}/issue/${openIssue.key}`}
          onClose={() => goToIssue(null)}
          onExpand={() => navigate(`/${domainName}/issue/${openIssue.key}`)}
          onDelete={(issueId: string) => {
            BoardService.deleteIssue(issueId);
            goToIssue(null);
          }}
          onPatchIssue={(patch: UpdateIssueInput) =>
            setOpenIssue((prev: Issue | null) => (prev ? { ...prev, ...patch } : prev))
          }
          boardRootRef={boardRootRef}
          descriptionReady={hydratedIssueId === openIssue.id}
        />
      )}

      <ShortcutsOverlay open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <AskAiModal open={askAiOpen} onClose={() => setAskAiOpen(false)} />
      {newIssueDraft && (
        <IssuePanel
          issue={newIssueDraft}
          statuses={config.statuses.filter((status: BoardStatus) => status.projectKey === newIssueDraft.projectKey)}
          project={config.projects.find((entry: Project) => entry.key === newIssueDraft.projectKey)}
          isNewIssue
          boardRootRef={boardRootRef}
          onClose={() => setNewIssueDraft(null)}
          onCreate={createFromDraft}
          newEpicChildren={newEpicChildren}
          onPatchIssue={(patch: UpdateIssueInput) =>
            setNewIssueDraft((prev: Issue | null) => (prev ? { ...prev, ...(patch as Partial<Issue>) } : prev))
          }
        />
      )}
      <ArchivedIssuesModal open={archivedOpen} board={activeBoard} onClose={() => setArchivedOpen(false)} />
      {importOpen && (
        <BoardImportWizard
          defaultProjectKey={activeBoard ? boardProjectKeys(activeBoard)[0] : undefined}
          onClose={() => setImportOpen(false)}
        />
      )}
      {undoToast && (
        <div className="kuika-board__undo-toast" role="status">
          <span>{undoToast.issueKey} archived</span>
          <button
            type="button"
            className="kuika-board__undo-toast-button"
            onClick={() => {
              BoardService.restoreIssue(undoToast.issueId);
              setUndoToast(null);
            }}
          >
            Undo
          </button>
        </div>
      )}
      {contextMenu &&
        (() => {
          const issue: IssueCardData | undefined = findLoadedCard(contextMenu.issueId);
          if (!issue) return null;
          const items: ContextMenuItem[] = [
            {
              label: "Open in drawer",
              icon: <KuikaSvg name="maximize" width={14} height={14} />,
              onSelect: () => openIssueCard(issue)
            },
            {
              label: "Open full page",
              icon: <KuikaSvg name="maximize" width={14} height={14} />,
              onSelect: () => navigate(`/${domainName}/issue/${issue.key}`),
              separatorAfter: true
            },
            // Only offer statuses the workflow allows moving to from the issue's current status.
            ...boardAvailableStatuses(activeBoard, config.statuses)
              .filter((status: BoardStatus) => {
                if (status.id === issue.statusId) return false;
                const targets: Set<string> | null = allowedTargetStatusIds(
                  config.projects.find((p: Project) => p.key === issue.projectKey),
                  issue.statusId
                );
                return targets === null || targets.has(status.id);
              })
              .map((status: BoardStatus) => ({
                label: `Move to ${status.name}`,
                icon: (
                  <span
                    className="kuika-board__column-status-dot"
                    style={{ background: statusDotColor(status.category), width: 8, height: 8 }}
                  />
                ),
                onSelect: () => {
                  flushPendingMove();
                  if (status.id !== issue.statusId) optimisticMove(issue, issue.statusId, status.id, 0);
                  BoardService.moveIssue({
                    issueId: issue.id,
                    destinationStatusId: status.id,
                    destinationIndex: 0,
                    sourceStatusId: issue.statusId
                  });
                }
              })),
            {
              label: "Assign to me",
              icon: <KuikaSvg name="user" width={14} height={14} />,
              onSelect: () => {
                optimisticPatch(issue, { assigneeUserId: currentUserId() });
                BoardService.updateIssue(issue.id, { assigneeUserId: currentUserId() });
              },
              separatorAfter: true
            },
            {
              label: "Copy issue key",
              icon: <KuikaSvg name="copy" width={14} height={14} />,
              onSelect: () => {
                void copyTextToClipboard(issue.key);
              }
            },
            {
              label: "Copy link",
              icon: <KuikaSvg name="copy" width={14} height={14} />,
              onSelect: () => {
                const url: string = `${window.location.origin}/${domainName}/issue/${issue.key}`;
                void copyTextToClipboard(url);
              },
              separatorAfter: true
            },
            {
              label: "Clone issue",
              icon: <KuikaSvg name="copy" width={14} height={14} />,
              onSelect: () => {
                void BoardService.cloneIssue(issue.id);
              },
              separatorAfter: true
            },
            {
              label: "Archive issue",
              danger: true,
              icon: <KuikaSvg name="archive" width={14} height={14} />,
              onSelect: () => {
                optimisticRemove(issue);
                BoardService.archiveIssue(issue.id);
                setUndoToast({ issueId: issue.id, issueKey: issue.key, expiresAt: Date.now() + 6000 });
              }
            }
          ];
          return <ContextMenu x={contextMenu.x} y={contextMenu.y} items={items} onClose={() => setContextMenu(null)} />;
        })()}
      <ConfirmDialogHost />
    </div>
  );
};

const Board = withManagement<{}>(BoardPage);
// The unwrapped page (no management shell) is reused by the in-designer board overlay.
export { Board, BoardPage };
export default Board;
